import { useEffect, useState } from 'react'
import { Alert, Button, Flex, InputNumber, Modal, Segmented, Space, Typography } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { formatDate } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { LoadError } from '@/shared/ui/LoadError'
import { fetchMyPrintSheet, type PrintSelection, type VoucherBatchRow, type VoucherPrintSheetData } from './voucherStockApi'
import {
  LAYOUTS,
  MAX_CARDS_PER_PRINT,
  PAGE,
  cardsPerPage,
  clampRange,
  loadLayoutChoice,
  paginate,
  saveLayoutChoice,
  sizeLabel,
  type SheetLayoutId,
} from './voucherPrintLayout'
import { imagesReady } from './printReady'
import { PrintPages, SheetPage, type CardMeta } from './VoucherSheetPages'

interface VoucherPrintSheetProps {
  /** `null` — oyna yopiq */
  batch: VoucherBatchRow | null
  /** Tanlangan kartalarning tartib raqamlari; `null` — partiya oraliq bilan chop etiladi */
  serials: readonly number[] | null
  /** Paketning foydalanuvchi tilidagi nomi; `null` — nom yo'q, `planId` chiqadi */
  planName: string | null
  onClose: () => void
}

function cardHintKey(sheet: VoucherPrintSheetData): string {
  if (sheet.credentialMode === 'LOGIN_PASSWORD') {
    return sheet.printQr ? 'myVouchers.print.cardHintLogin' : 'myVouchers.print.cardHintLoginNoQr'
  }
  return sheet.printQr ? 'myVouchers.print.cardHint' : 'myVouchers.print.cardHintNoQr'
}

/**
 * Kartalarni A4 varaqqa chop etish — butun partiya (oraliq bilan) yoki
 * tanlangan kartalar. Geometriya va karta maketi admin paneli bilan bir
 * xil (`voucherPrintLayout.ts`, `VoucherSheetPages.tsx`). Kodlar serverdan
 * olinadi va har so'rov auditga yoziladi.
 *
 * Kartada tashkilot nomi chiqadi — egasi talabi: kartani topgan odam
 * kimga murojaat qilishini bilsin.
 */
export function VoucherPrintSheet({ batch, serials, planName, onClose }: VoucherPrintSheetProps) {
  const { t } = useTranslation()
  const [range, setRange] = useState({ from: 1, to: 1 })
  const [sheet, setSheet] = useState<VoucherPrintSheetData | null>(null)
  const [logoFailed, setLogoFailed] = useState(false)
  const [layoutId, setLayoutId] = useState<SheetLayoutId>(loadLayoutChoice)
  const layout = LAYOUTS[layoutId]

  const load = useMutation({
    mutationFn: (request: { batchId: string; selection: PrintSelection }) =>
      fetchMyPrintSheet(request.batchId, request.selection),
    onSuccess: setSheet,
  })
  const { mutate, reset } = load

  const batchId = batch?.id ?? null
  const quantity = batch?.quantity ?? 0

  useEffect(() => {
    setSheet(null)
    setLogoFailed(false)
    reset()
    if (batchId === null) return
    if (serials !== null) {
      mutate({ batchId, selection: { serials } })
      return
    }
    const initial = clampRange(1, quantity, quantity)
    setRange(initial)
    if (quantity <= MAX_CARDS_PER_PRINT) mutate({ batchId, selection: initial })
  }, [batchId, quantity, serials, mutate, reset])

  const requestRange = (): void => {
    if (batchId === null) return
    const next = clampRange(range.from, range.to, quantity)
    setRange(next)
    setSheet(null)
    mutate({ batchId, selection: next })
  }

  const chooseLayout = (id: SheetLayoutId): void => {
    setLayoutId(id)
    saveLayoutChoice(id)
  }

  const pages = sheet ? paginate(sheet.cards, layout) : []
  const meta: CardMeta | null = sheet
    ? {
        batchNo: sheet.batchNo,
        quantity: sheet.quantity,
        note: sheet.note,
        planName: planName ?? sheet.planId,
        price: formatMoneyValue(sheet.price),
        validUntil: sheet.validUntil
          ? t('myVouchers.print.validUntil', { date: formatDate(sheet.validUntil) })
          : null,
        issuer: sheet.companyName,
        branch: sheet.branchName,
        logoUrl: sheet.logoUrl,
        instructions: sheet.instructions,
        hint: t(cardHintKey(sheet)),
        portalUrl: sheet.portalUrl,
        printQr: sheet.printQr,
        loginLabel: t('myVouchers.print.login'),
        passwordLabel: t('myVouchers.print.password'),
      }
    : null

  const print = async (): Promise<void> => {
    const ready = await imagesReady()
    setLogoFailed(!ready)
    if (ready) window.print()
  }

  return (
    <Modal
      open={batch !== null}
      title={sheet ? t('myVouchers.print.titleNo', { no: sheet.batchNo }) : t('myVouchers.print.title')}
      okText={t('myVouchers.print.print')}
      cancelText={t('common.close')}
      onOk={() => void print()}
      onCancel={onClose}
      okButtonProps={{ disabled: pages.length === 0 }}
      width={880}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex gap={8} align="center" wrap>
          <Typography.Text>{t('myVouchers.print.layout')}</Typography.Text>
          <Segmented<SheetLayoutId>
            value={layoutId}
            onChange={chooseLayout}
            options={[
              { value: 'compact', label: t('myVouchers.print.layoutCompact', { perPage: cardsPerPage(LAYOUTS.compact) }) },
              { value: 'standard', label: t('myVouchers.print.layoutStandard', { perPage: cardsPerPage(LAYOUTS.standard) }) },
            ]}
          />
        </Flex>
        {serials === null ? (
          <>
            <Flex gap={8} align="center" wrap>
              <Typography.Text>{t('myVouchers.print.range')}</Typography.Text>
              <InputNumber
                min={1}
                max={quantity}
                value={range.from}
                onChange={(value) => setRange((r) => ({ ...r, from: value ?? 1 }))}
                aria-label={t('myVouchers.print.rangeFrom')}
              />
              <Typography.Text type="secondary">—</Typography.Text>
              <InputNumber
                min={1}
                max={quantity}
                value={range.to}
                onChange={(value) => setRange((r) => ({ ...r, to: value ?? r.from }))}
                aria-label={t('myVouchers.print.rangeTo')}
              />
              <Button onClick={requestRange} loading={load.isPending}>
                {t('myVouchers.print.load')}
              </Button>
            </Flex>
            <Typography.Text type="secondary">
              {t('myVouchers.print.rangeHint', { max: MAX_CARDS_PER_PRINT, quantity })}
            </Typography.Text>
          </>
        ) : null}

        {load.isError ? <LoadError titleKey="myVouchers.print.loadFailed" error={load.error} /> : null}

        {logoFailed ? (
          <Alert
            type="error"
            showIcon
            message={t('myVouchers.print.logoFailedTitle')}
            description={t('myVouchers.print.logoFailedText')}
          />
        ) : null}

        {sheet && sheet.printQr && !sheet.portalUrl ? (
          <Alert
            type="warning"
            showIcon
            message={t('myVouchers.print.noPortalTitle')}
            description={t('myVouchers.print.noPortalText')}
          />
        ) : null}

        {sheet && sheet.cards.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            message={t('myVouchers.print.warnTitle')}
            description={
              <>
                {t(sheet.credentialMode === 'LOGIN_PASSWORD' ? 'myVouchers.print.warnTextCredentials' : 'myVouchers.print.warnText', {
                  count: sheet.cards.length,
                })}{' '}
                {t('myVouchers.print.pages', { count: pages.length, perPage: cardsPerPage(layout), size: sizeLabel(layout) })}
                <br />
                {t('myVouchers.print.printTip')}
              </>
            }
          />
        ) : null}

        {sheet && sheet.skipped > 0 ? (
          <Alert type="info" showIcon message={t('myVouchers.print.skipped', { count: sheet.skipped })} />
        ) : null}

        {sheet && sheet.cards.length === 0 ? (
          <Typography.Text type="secondary">{t('myVouchers.print.nothing')}</Typography.Text>
        ) : null}

        {meta && pages[0] ? (
          <div style={{ overflowX: 'auto' }}>
            <Typography.Text type="secondary">{t('myVouchers.print.preview')}</Typography.Text>
            <div style={{ boxShadow: '0 0 0 1px #d9d9d9', width: `${PAGE.widthMm}mm`, marginTop: 8 }}>
              <SheetPage cards={pages[0]} meta={meta} layout={layout} />
            </div>
          </div>
        ) : null}
      </Space>

      {meta && sheet && sheet.cards.length > 0 ? <PrintPages cards={sheet.cards} meta={meta} layout={layout} /> : null}
    </Modal>
  )
}
